import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { strokePoints } from '../../store/illustrations.js';

// One page of the assessment, at a fixed size.
//
// The meeting shows it scaled to the screen and the takeaway prints it scaled
// to the sheet, but its layout is worked out once, at 1120 by 766, the
// proportions of a letter-landscape page less its footer. Nothing inside a
// slide depends on the width of the window, so a mark drawn over a figure in
// the room lands on the same figure on paper, and a page with drawing on it
// looks exactly like a page without.
//
// Ink is stored as fractions of the slide's width on both axes and painted in
// the slide's own units, so it scales with the page rather than the screen.

export const SLIDE_W = 1120;
export const SLIDE_H = 766;

export function SlideInk({ strokes = [], live = null, className = '', style }) {
  const all = live ? [...strokes, live] : strokes;
  return (
    <svg
      className={`conv-ink conv-slide-ink ${className}`}
      style={style}
      viewBox={`0 0 ${SLIDE_W} ${SLIDE_H}`}
      preserveAspectRatio="none"
      aria-hidden={all.length ? undefined : 'true'}
      role={all.length ? 'img' : undefined}
      aria-label={all.length ? `${strokes.length} advisor annotation${strokes.length === 1 ? '' : 's'}` : undefined}
    >
      {all.map((s, i) => (
        <polyline
          key={i}
          points={strokePoints(s, SLIDE_W)}
          fill="none"
          stroke={s.c}
          strokeWidth={s.w || 3}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

// A page that runs longer than the slide is scaled down to fit, the way a
// presentation's autofit does, so nothing is ever cut off. The measurement is
// of layout, not of the screen, so the meeting and the print always agree.
function FitBody({ children }) {
  const body = useRef(null);
  const content = useRef(null);

  useLayoutEffect(() => {
    const b = body.current;
    const c = content.current;
    if (!b || !c) return undefined;
    const fit = () => {
      c.style.transform = '';
      c.style.width = '';
      const cs = getComputedStyle(b);
      const avail = b.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      const natural = c.scrollHeight;
      if (!(avail > 0) || natural <= avail + 1) return;
      const s = Math.max(0.6, avail / natural);
      c.style.width = `${100 / s}%`;
      c.style.transform = `scale(${s.toFixed(4)})`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(c);
    return () => ro.disconnect();
  });

  return (
    <div className="conv-slide-body" ref={body}>
      <div className="conv-slide-content" ref={content}>{children}</div>
    </div>
  );
}

// Below this the type is too small to present from, so a very short window
// scrolls instead of shrinking the page further.
const MIN_FIT = 0.5;
// Room kept either side of the page in the meeting, for the marker's controls
// and for drawing in the margin.
const FIT_MARGIN = 76;

/**
 * The slide, scaled to the width it is given. `ref` is the slide itself, the
 * surface a marker draws on.
 *
 * With `fit`, as in the meeting, the whole page is scaled to fit the space it
 * is given both ways, with a margin either side, so it is always entirely on
 * screen (Jimmy, 2026-10-02). It is the same fixed page the takeaway prints,
 * which is what keeps a drawing exactly where it was made. `beside` is laid
 * over the page's own box, for controls that sit in its margins, and `margin`
 * is how much room either side is kept for them: a whiteboard keeps none.
 */
const Slide = forwardRef(function Slide({ children, strokes = [], live = null, maxScale = 1.4, minScale = MIN_FIT, className = '', fit = false, margin = FIT_MARGIN, beside = null }, ref) {
  const frame = useRef(null);
  const box = useRef(null);
  const inner = useRef(null);
  const [scale, setScale] = useState(0);

  const setSlide = el => {
    inner.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  useLayoutEffect(() => {
    const el = fit ? box.current : frame.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      if (!(w > 0)) return;
      if (!fit) { setScale(Math.min(maxScale, w / SLIDE_W)); return; }
      const byWidth = Math.min(maxScale, Math.max(0, w - 2 * margin) / SLIDE_W);
      const h = el.clientHeight;
      const byHeight = h > 0 ? h / SLIDE_H : byWidth;
      // Never wider than the space it is given: a phone held upright is
      // narrower than half a page, and the page used to run off its side
      // (2026-10-04). A short window still scrolls down rather than shrink
      // the type below `minScale`, which a phone or tablet sets to nothing.
      setScale(Math.min(byWidth, Math.max(byHeight, Math.min(minScale, byWidth))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxScale, minScale, fit, margin]);

  const k = scale || 1;
  // In the meeting the ink sits over the page on its own layer, as wide as the
  // white area, so a mark in the margin is not cut off at the page's edge.
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    if (!fit) return;
    const f = frame.current;
    const b = box.current;
    if (f && b) setOffset(f.getBoundingClientRect().left - b.getBoundingClientRect().left);
  });
  const slide = (
    <div
      className={`conv-slide-frame ${className}`}
      ref={frame}
      style={fit ? { width: SLIDE_W * k, height: SLIDE_H * k } : { height: SLIDE_H * k, maxWidth: SLIDE_W * maxScale }}
    >
      <div className="conv-slide" ref={setSlide} style={{ transform: `scale(${k})`, visibility: scale ? undefined : 'hidden' }}>
        <FitBody>{children}</FitBody>
        {fit ? null : <SlideInk strokes={strokes} live={live} />}
      </div>
    </div>
  );
  if (!fit) return slide;
  return (
    <div className="conv-slide-fit" ref={box}>
      {slide}
      <SlideInk
        strokes={strokes}
        live={live}
        className="is-over"
        style={{ left: offset, width: SLIDE_W * k, height: SLIDE_H * k, visibility: scale ? undefined : 'hidden' }}
      />
      {beside ? (
        <div
          className="conv-slide-beside"
          style={{ left: offset, width: SLIDE_W * k, height: SLIDE_H * k, visibility: scale ? undefined : 'hidden' }}
        >
          {beside}
        </div>
      ) : null}
    </div>
  );
});

export default Slide;
